// Utility functions for handling delays and timing

/**
 * Creates a delay using Promise-based approach
 * @param ms - Delay in milliseconds
 * @returns Promise that resolves after the specified delay
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Executes a callback after a specified delay
 * @param callback - Function to execute after delay
 * @param ms - Delay in milliseconds
 * @returns Timeout ID that can be used to cancel the delay
 */
export const delayedExecution = (callback: () => void, ms: number): NodeJS.Timeout => {
  return setTimeout(callback, ms);
};

/**
 * Executes multiple callbacks with different delays in sequence
 * @param tasks - Array of objects containing callback and delay
 * @example
 * executeWithDelays([
 *   { callback: () => console.log('First'), delay: 50 },
 *   { callback: () => console.log('Second'), delay: 100 }
 * ]);
 */
export const executeWithDelays = (tasks: Array<{ callback: () => void; delay: number }>): void => {
  let accumulatedDelay = 0;
  
  tasks.forEach(task => {
    accumulatedDelay += task.delay;
    delayedExecution(task.callback, accumulatedDelay);
  });
};

/**
 * Async version of executeWithDelays using Promise-based delays
 * @param tasks - Array of objects containing callback and delay
 */
export const executeWithDelaysAsync = async (tasks: Array<{ callback: () => void; delay: number }>): Promise<void> => {
  for (const task of tasks) {
    await delay(task.delay);
    task.callback();
  }
};
